import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Stat, Section, Loading, Empty, useFetch, dt } from "./_shared";

export default function Monitoring() {
  const { data: ov, loading } = useFetch("/admin/monitoring/overview");
  const { data: cap } = useFetch("/admin/monitoring/capacity");
  const { data: errData, reload: reloadErr } = useFetch("/admin/monitoring/errors");
  const { data: sched } = useFetch("/admin/monitoring/scheduler");
  const [busy, setBusy] = useState(null);
  if (loading) return <Loading />;
  const h = ov?.health || {}; const services = ov?.services || [];
  const metrics = cap?.metrics || []; const errors = errData?.errors || [];

  const resolve = async (eid) => {
    setBusy(eid);
    try { await api.post(`/admin/monitoring/errors/${eid}/resolve`); toast.success("Fehler als gelöst markiert."); await reloadErr(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Monitoring" subtitle="Dienste, Kapazität, Fehler & Scheduler." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Health-Score" value={h.score != null ? `${h.score}` : "—"} tone={h.score >= 90 ? "good" : h.score >= 70 ? "warn" : "bad"} hint={h.label} testId="admin-mon-health" />
        <Stat label="Kritisch down" value={h.critical_down ?? 0} tone={h.critical_down ? "bad" : "good"} />
        <Stat label="Offene Fehler" value={h.total_errors_open ?? 0} tone={h.critical_errors_open ? "bad" : (h.total_errors_open ? "warn" : "good")} />
        <Stat label="Aktive Nutzer 24h" value={h.active_users_24h ?? 0} />
      </div>

      <Section title="Dienste" testId="admin-mon-services">
        <div className="grid gap-2 sm:grid-cols-2">
          {services.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-2 rounded-xl border border-border bg-background p-3">
              <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{s.name}</p><p className="truncate text-xs text-muted-foreground">{s.note}{s.latency_ms ? ` · ${s.latency_ms}ms` : ""}</p></div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.status === "up" ? "bg-emerald-100 text-emerald-700" : s.status === "down" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Kapazität" testId="admin-mon-capacity">
        <div className="space-y-2">
          {metrics.map((m) => (
            <div key={m.key} className="rounded-xl border border-border bg-background p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">{m.label}</p>
                <span className={`text-xs font-semibold ${m.severity === "critical" ? "text-rose-600" : m.severity === "warning" ? "text-amber-600" : "text-emerald-600"}`}>{m.used_pct}%</span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                <div className={`h-full ${m.severity === "critical" ? "bg-rose-500" : m.severity === "warning" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, m.used_pct)}%` }} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{m.message}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Fehler (${errors.length})`} testId="admin-mon-errors">
        {errors.length === 0 ? <Empty text="Keine offenen Fehler." /> : (
          <div className="space-y-2">
            {errors.slice(0, 30).map((e) => {
              const id = e._id || e.id;
              return (
                <div key={id} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-background p-3">
                  <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{e.level || "ERROR"} · {e.message || e.error || e.path || "—"}</p><p className="truncate text-xs text-muted-foreground">{dt(e.last_seen || e.timestamp)}{e.count ? ` · ${e.count}×` : ""}</p></div>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={busy === id} onClick={() => resolve(id)} data-testid={`admin-mon-resolve-${id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {sched && (
        <Section title={`Scheduler (${sched.total || 0} Jobs)`} testId="admin-mon-scheduler">
          <div className="space-y-1.5">
            {(sched.jobs || []).slice(0, 40).map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-2 text-sm"><span className="truncate text-foreground">{j.id}</span><span className="shrink-0 text-xs text-muted-foreground">{j.next_run ? dt(j.next_run) : "—"}</span></div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
